import { z } from 'zod'
import { treeDtoSchema, type TreeDto } from '@lib/dto/tree.dto'
import { treeSyncOperationSchema, type TreeSyncOperation } from '@lib/input/tree.input'
import logger, { toErrorMessage } from '@/helpers/logger'
import { noteSurveyDraftStored } from '@/helpers/surveyStorage'
import { answerOf, sameAnswer, withAnswer } from '@/site-visits/changeAnswer'
import { isForVisit, type SurveyVisit } from '@/site-visits/surveyVisit'

/**
 * Offline outbox for a site survey.
 *
 * The survey screen is used standing in a park, and parks are exactly where a
 * phone drops to one bar. A capture flow that writes straight to the network
 * loses a tree the moment signal does — while someone is standing under it,
 * which is the one moment the data cannot be recreated from a desk.
 *
 * So the local copy is authoritative for the UI, and every change is also
 * appended here as an operation to be replayed. The server is caught up when it
 * can be. Nothing about the screen changes when the network comes and goes; the
 * only visible difference is a count of what is still waiting.
 */

/**
 * A queued operation with a monotonic sequence number.
 *
 * The number is what makes a flush safe to run concurrently with more editing:
 * the flush claims everything up to a high-water mark, and operations recorded
 * while it is in flight get higher numbers and survive it. Without that, edits
 * made during a slow request would be silently dropped when the response
 * cleared "the queue".
 *
 * `snapshot` is the full tree the map was drawing when the edit was queued.
 * An operation carries only what the API writes, so without the snapshot a
 * visit change has nothing to put back on screen: the working set is cleared
 * so the previous visit is not drawn as this one, and layering the queue
 * afterwards would put that cleared copy back on top of the server. The
 * snapshot stays off the schema on purpose. A display copy this build cannot
 * read must cost the copy, not the operation, which is the only thing that
 * can still be replayed.
 */
const queuedOperationSchema = z.object({
  seq: z.number().int().nonnegative(),
  operation: treeSyncOperationSchema,
})

/** The queue entry as stored, plus the display copy when one survived. */
export interface QueuedOperation {
  seq: number
  operation: TreeSyncOperation
  /**
   * The tree as the map drew it when this edit was queued. Absent on an
   * archive, on a queue written before snapshots, or when the copy could
   * not be read back.
   */
  snapshot?: TreeDto
}

/**
 * A queue entry with its display copy left unparsed.
 *
 * The operation is judged on its own. A snapshot this build cannot read is
 * dropped afterwards, so it cannot fail the entry the way a strict field on
 * this object would.
 */
const queuedItemSchema = z.object({
  seq: z.number().int().nonnegative(),
  operation: z.unknown(),
  snapshot: z.unknown().optional(),
})

/**
 * The rows and the queue. Visit identity is a separate union below, because
 * "fresh" only means something once a visit has been named.
 */
interface SurveyCacheBody {
  /** The working set the map draws: server truth with local edits applied. */
  trees: TreeDto[]
  queue: QueuedOperation[]
  nextSeq: number
}

/**
 * A cache, one shape per what it knows about visits.
 *
 * A plain interface rather than an inferred schema, because nothing validates
 * the cache as a single unit — and nothing should. Each tree and each queued
 * operation is parsed on its own, so that one item an older build wrote costs
 * that item instead of the whole survey.
 *
 * `visitId` absent means a cache written before visits existed, whose findings
 * are the site's one migrated visit's. Null means no visit. A string is the
 * visit whose findings are shown. `fresh` exists only in the second shape:
 * it says those findings are known to be that visit's (folded in from the
 * server, or a visit started on this phone, which has nothing found yet).
 * Until they are, they are shown but not edited, so a stale view is never
 * sent as this visit's. See docs/site-visits.md.
 */
export type SurveyCache = SurveyCacheBody &
  (
    | { visitId?: undefined; fresh?: undefined }
    | { visitId: string | null; fresh?: boolean }
  )

export const EMPTY_CACHE: SurveyCache = { trees: [], queue: [], nextSeq: 1 }

/**
 * Copy a cache and change some of it, keeping the visit shapes apart.
 *
 * Spreading the union widens `visitId` until it fits neither shape. Building
 * the result from the fields, after deciding whether a visit has been named,
 * is what lets the caller change the queue without a cast.
 *
 * A `fresh` flag on a cache that still has no visit is dropped. The flag
 * would say the findings belong to a visit the cache has not chosen.
 */
function cacheWith(
  cache: SurveyCache,
  patch: {
    trees?: TreeDto[]
    queue?: QueuedOperation[]
    nextSeq?: number
    visitId?: string | null
    fresh?: boolean
  },
): SurveyCache {
  const trees = patch.trees ?? cache.trees
  const queue = patch.queue ?? cache.queue
  const nextSeq = patch.nextSeq ?? cache.nextSeq
  const visitId = patch.visitId === undefined ? cache.visitId : patch.visitId
  const fresh = patch.fresh === undefined ? cache.fresh : patch.fresh
  if (visitId === undefined) return { trees, queue, nextSeq }
  if (fresh === undefined) return { trees, queue, nextSeq, visitId }
  return { trees, queue, nextSeq, visitId, fresh }
}

/**
 * The working set is now known to be a visit's own.
 *
 * A cache from before visits has no `visitId`. Naming it here is what makes
 * `fresh` meaningful. A later confirmation does not rename a visit the phone
 * has already chosen: null is "no visit", which is a choice, not a blank.
 */
/** The same cache, showing a different working set. The queue is unchanged. */
export function withTrees(cache: SurveyCache, trees: TreeDto[]): SurveyCache {
  return cacheWith(cache, { trees })
}

/** The same cache, with a different queue. The visit it shows is unchanged. */
export function withQueue(
  cache: SurveyCache,
  queue: QueuedOperation[],
): SurveyCache {
  return cacheWith(cache, { queue })
}

export function withServerFindings(
  cache: SurveyCache,
  trees: TreeDto[],
  visitId: string | null,
  queue: QueuedOperation[] = cache.queue,
): SurveyCache {
  return cacheWith(cache, {
    trees,
    queue,
    visitId: cache.visitId === undefined ? visitId : cache.visitId,
    fresh: true,
  })
}

/**
 * The envelope, validated without looking inside the two arrays.
 *
 * Separating the envelope from its contents is what makes salvage possible:
 * each tree and each queued operation is then judged on its own, so one item
 * this build cannot read costs that item rather than the morning.
 */
const cacheEnvelopeSchema = z.object({
  trees: z.array(z.unknown()).default([]),
  queue: z.array(z.unknown()).default([]),
  nextSeq: z.number().int().nonnegative().optional(),
  visitId: z.string().nullable().optional(),
  fresh: z.boolean().optional(),
})

export interface LoadedCache {
  cache: SurveyCache
  /**
   * Queued operations that could not be read back — work this device recorded
   * and can no longer replay.
   *
   * Counted separately from unreadable `trees`, which cost nothing: the
   * working set is re-fetched from the server on the next load, so a display
   * row this build cannot parse is a row it is about to replace anyway. A
   * dropped QUEUE entry is the opposite — it is the only copy.
   */
  lostOperations: number
}

function storageKey(siteId: string): string {
  return `swocrm.survey.${siteId}.v1`
}

/**
 * Where an unreadable payload is kept instead of being overwritten.
 *
 * A queue that cannot be replayed by this build may still be readable by a
 * person, and a surveyor who has lost a morning is owed the chance to get it
 * back by hand. Overwriting it with an empty cache — which is what happens the
 * instant the screen renders and persists its state — forecloses that.
 */
function quarantineKey(siteId: string): string {
  return `swocrm.survey.${siteId}.v1.unreadable`
}

/** Keep a payload we are about to replace with something lossier. */
function quarantine(siteId: string, raw: string): void {
  try {
    window.localStorage.setItem(quarantineKey(siteId), raw)
  } catch (caught) {
    logger.error('survey: could not quarantine an unreadable cache', {
      siteId,
      reason: toErrorMessage(caught),
    })
  }
}

/**
 * Read a site's cache, salvaging whatever this build can still understand.
 *
 * The rule is that a shape change must never cost queued work silently. It
 * used to: `trees` and `queue` were validated as a single object, so one
 * display row left by an older build failed the whole parse and took the queue
 * with it — and because the screen persists its state as soon as it renders,
 * the empty result was written straight back over the original. A survey could
 * be destroyed by opening the page, and the indicator then read "Saved",
 * because nothing was pending any more.
 *
 * So each item is parsed on its own, anything unreadable is quarantined rather
 * than dropped, and the count of lost operations is returned so the caller can
 * say so out loud instead of showing a tick.
 */
export function loadCache(siteId: string): LoadedCache {
  const intact = (cache: SurveyCache): LoadedCache => ({
    cache,
    lostOperations: 0,
  })

  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(storageKey(siteId))
  } catch (caught) {
    logger.error('survey: could not read cache', {
      siteId,
      reason: toErrorMessage(caught),
    })
    return intact(EMPTY_CACHE)
  }
  if (raw === null) return intact(EMPTY_CACHE)

  const envelope = parseEnvelope(raw)
  if (envelope === null) {
    // Not even an object with the right two arrays. Nothing to salvage from,
    // but the bytes are kept in case a person can make sense of them.
    logger.error('survey: cache is unreadable, quarantining it', { siteId })
    quarantine(siteId, raw)
    return { cache: EMPTY_CACHE, lostOperations: 0 }
  }

  const trees: TreeDto[] = []
  for (const candidate of envelope.trees) {
    const parsed = treeDtoSchema.safeParse(candidate)
    if (parsed.success) trees.push(parsed.data)
  }

  const queue: QueuedOperation[] = []
  let lostOperations = 0
  for (const candidate of envelope.queue) {
    const parsed = parseQueued(candidate)
    if (parsed !== null) {
      queue.push(parsed)
      continue
    }

    lostOperations += 1
    logger.error('survey: discarding an unreadable queued operation', {
      siteId,
    })
  }

  if (lostOperations > 0 || trees.length < envelope.trees.length) {
    quarantine(siteId, raw)
  }

  /*
   * A sequence number must never be re-used: `enqueue` collapses by tree id and
   * the flush claims everything at or below a high-water mark, so a number that
   * comes around twice can retire an operation recorded after it. Recomputing
   * from what survived is safe where trusting a missing field is not.
   */
  const highestSeq = queue.reduce((max, queued) => Math.max(max, queued.seq), 0)
  const nextSeq = Math.max(envelope.nextSeq ?? 1, highestSeq + 1)

  // A fresh flag with no visit says the findings belong to a visit this cache
  // never named. It is not kept: the next open treats the cache as pre-visit
  // and waits for the server anyway.
  const storedVisitId = envelope.visitId
  const storedFresh = envelope.fresh
  if (storedVisitId === undefined) {
    return {
      cache: cacheWith(EMPTY_CACHE, { trees, queue, nextSeq }),
      lostOperations,
    }
  }
  if (storedFresh === undefined) {
    return {
      cache: cacheWith(EMPTY_CACHE, {
        trees,
        queue,
        nextSeq,
        visitId: storedVisitId,
      }),
      lostOperations,
    }
  }
  return {
    cache: cacheWith(EMPTY_CACHE, {
      trees,
      queue,
      nextSeq,
      visitId: storedVisitId,
      fresh: storedFresh,
    }),
    lostOperations,
  }
}

/**
 * A queued entry as builds before the `problems` change wrote it.
 *
 * Only the three fields that moved are described; everything else passes
 * through untouched, because this exists to convert a shape, not to
 * re-validate a payload the real schema checks immediately afterwards.
 */
const legacyQueuedSchema = z.object({
  seq: z.number().int().nonnegative(),
  operation: z.object({
    op: z.literal('upsert'),
    tree: z
      .object({
        hasProblem: z.boolean(),
        problemNotes: z.string().nullish(),
        problemSeverity: z.number().int().nullish(),
      })
      .passthrough(),
  }),
})

/**
 * Bring a queued operation written by an older build up to the current shape.
 *
 * The survey's write format changed when a tree's single problem flag became a
 * list of problems, and a queue recorded before that could no longer be read —
 * so it was quarantined and reported as lost, which is exactly what happened
 * to somebody's afternoon. Quarantining meant it was recoverable; it should
 * never have needed recovering.
 *
 * The mapping is the one the database migration already used for the server's
 * own rows: a flagged tree becomes one "Other" problem carrying whatever was
 * typed. Tried only after the current schema has refused the operation, so a
 * well-formed one is never touched — and returning null for anything this does
 * not recognize, because a shape from some future build read by an older one
 * is not something to guess at.
 */
function migrateLegacyQueued<T>(candidate: T): QueuedOperation | null {
  const legacy = legacyQueuedSchema.safeParse(candidate)
  if (!legacy.success) return null

  const { hasProblem, problemNotes, problemSeverity, ...tree } =
    legacy.data.operation.tree
  const notes = problemNotes?.trim() ?? ''

  const rebuilt = queuedOperationSchema.safeParse({
    seq: legacy.data.seq,
    operation: {
      op: 'upsert',
      tree: {
        ...tree,
        problems: hasProblem
          ? [
              {
                id: crypto.randomUUID(),
                typeId: null,
                // "Other" requires a note, and a tree flagged with nothing
                // typed is a real state — somebody ticked the box and moved
                // on. Saying so beats dropping the finding for want of a
                // sentence.
                notes:
                  notes === ''
                    ? 'Recorded as a problem, no details given'
                    : notes,
                severity: problemSeverity ?? null,
              },
            ]
          : [],
      },
    },
  })
  if (!rebuilt.success) return null
  logger.warn('survey: migrated a queued operation from an older build', {
    seq: legacy.data.seq,
  })
  return rebuilt.data
}

/**
 * One queue entry, or null when the operation itself cannot be replayed.
 *
 * A snapshot that does not parse is dropped and the operation is kept. The
 * operation is the only copy of the edit; the snapshot is only how to draw
 * it until the server answers.
 */
function parseQueued(
  candidate: z.infer<typeof cacheEnvelopeSchema>['queue'][number],
): QueuedOperation | null {
  const envelope = queuedItemSchema.safeParse(candidate)
  if (!envelope.success) return migrateLegacyQueued(candidate)

  const operation = treeSyncOperationSchema.safeParse(envelope.data.operation)
  if (!operation.success) return migrateLegacyQueued(candidate)

  // An older build logs the conversion itself. A current one that merely
  // lost its display copy does not: the edit is still here.
  if (operation.data.op !== 'upsert' || envelope.data.snapshot === undefined) {
    return { seq: envelope.data.seq, operation: operation.data }
  }
  const snapshot = treeDtoSchema.safeParse(envelope.data.snapshot)
  if (!snapshot.success) {
    return { seq: envelope.data.seq, operation: operation.data }
  }
  return {
    seq: envelope.data.seq,
    operation: operation.data,
    snapshot: snapshot.data,
  }
}

/** The envelope, or null when the payload is not one. */
function parseEnvelope(raw: string): z.infer<typeof cacheEnvelopeSchema> | null {
  try {
    const parsed = cacheEnvelopeSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

/** True when the phone kept the draft. A full disk or a private window throws. */
export function saveCache(siteId: string, cache: SurveyCache): boolean {
  try {
    window.localStorage.setItem(storageKey(siteId), JSON.stringify(cache))
    noteSurveyDraftStored(true)
    return true
  } catch (caught) {
    noteSurveyDraftStored(false)
    logger.error('survey: could not save cache', {
      siteId,
      reason: toErrorMessage(caught),
    })
    return false
  }
}

/** The tree an operation concerns, whichever kind it is. */
export function operationTreeId(operation: TreeSyncOperation): string {
  return operation.op === 'archive' ? operation.id : operation.tree.id
}

/**
 * Append an operation, collapsing any earlier one for the same tree and visit.
 *
 * The queue holds intentions, not history: eleven edits to tree #41 while
 * offline are one statement about what #41 should look like. Collapsing keeps a
 * long offline session's queue proportional to the number of trees touched
 * rather than to the number of taps, and makes replay cheaper on the server.
 *
 * By visit as well as tree, because an edit carries the findings of the visit
 * it was made in. Finishing #41 on one visit and starting the next before the
 * phone has signal leaves two statements, one per visit, and collapsing them
 * would lose the first. An archive supersedes everything queued for the tree.
 *
 * And keeping the answer to "Was it wrong, or did it change?": a later edit
 * that asked nothing takes on the earlier one's answer, so "Missed last time"
 * on a new tree is still with it after its species is filled in. Two
 * different answers stay two statements, so the server logs each part of the
 * change as what it was.
 */
export function enqueue(
  cache: SurveyCache,
  operation: TreeSyncOperation,
  /** The tree as drawn, so a later visit can put this edit back. */
  snapshot?: TreeDto,
): SurveyCache {
  const treeId = operationTreeId(operation)
  let answer = answerOf(operation)
  // Newest first, so an unanswered edit takes the latest answer, and stops
  // at a statement answered differently: it and those before it stand.
  const replaced = new Set<number>()
  let answeredApart = false
  for (const queued of [...cache.queue].reverse()) {
    if (operationTreeId(queued.operation) !== treeId) continue
    if (operation.op === 'archive' || queued.operation.op === 'archive') {
      replaced.add(queued.seq)
      continue
    }
    if (answeredApart || queued.operation.visitId !== operation.visitId) {
      continue
    }
    const earlier = answerOf(queued.operation)
    if (
      earlier !== undefined &&
      answer !== undefined &&
      !sameAnswer(earlier, answer)
    ) {
      answeredApart = true
      continue
    }
    answer ??= earlier
    replaced.add(queued.seq)
  }
  const queue = cache.queue.filter((queued) => !replaced.has(queued.seq))
  const collapsed: TreeSyncOperation =
    answer === undefined || operation.op === 'archive'
      ? operation
      : withAnswer(operation, answer)
  // Replacing an edit replaces its picture of the tree. An archive has no
  // findings to remember, and a snapshot of a different tree would draw the
  // wrong one when the visit comes back.
  const queued: QueuedOperation =
    snapshot === undefined ||
    collapsed.op !== 'upsert' ||
    snapshot.id !== collapsed.tree.id
      ? { seq: cache.nextSeq, operation: collapsed }
      : { seq: cache.nextSeq, operation: collapsed, snapshot }
  return cacheWith(cache, {
    queue: [...queue, queued],
    nextSeq: cache.nextSeq + 1,
  })
}

/** A tree with nothing found on it yet: how every tree reads on a new visit. */
export function withoutFindings(tree: TreeDto): TreeDto {
  return {
    ...tree,
    estimatedLaborMinutes: null,
    removalOffered: false,
    removalMinutes: null,
    removalNotes: null,
    noPruningRecommended: false,
    pruningPriceOverride: null,
    removalPriceOverride: null,
    priceOverrideNotes: null,
    isOptional: false,
    problems: [],
    jobs: [],
  }
}

/**
 * Copy a queued tree's findings onto the operation before the working set
 * is cleared.
 *
 * Only from a working set that is still that visit's. After a visit change
 * the trees on screen are stripped, and copying those would freeze "nothing
 * found" as what the earlier visit recorded — then the next server fold
 * would paint that over the real findings. An edit that already has a
 * snapshot keeps it: a later, stripped copy must not replace it.
 */
function rememberTreeSnapshots(cache: SurveyCache): SurveyCache {
  let changed = false
  const queue = cache.queue.map((queued) => {
    if (queued.operation.op !== 'upsert' || queued.snapshot !== undefined) {
      return queued
    }
    const recordedOn = queued.operation.visitId ?? null
    if (cache.visitId !== undefined && (cache.visitId ?? null) !== recordedOn) {
      return queued
    }
    const treeId = queued.operation.tree.id
    const tree = cache.trees.find((candidate) => candidate.id === treeId)
    if (tree === undefined) return queued
    changed = true
    return { ...queued, snapshot: tree }
  })
  if (!changed) return cache
  return cacheWith(cache, { queue })
}

/**
 * The trees to draw in a visit: every pin, with findings only from edits
 * that were queued for this visit.
 *
 * Starts stripped, so a tree recorded on the visit just left does not keep
 * that visit's jobs on screen. A queued edit for the visit being opened is
 * put back from its snapshot. The stripped working-set row is not a
 * fallback: that is the copy that lost the findings.
 */
function treesForVisit(
  cache: SurveyCache,
  visit: SurveyVisit | null,
): TreeDto[] {
  const byId = new Map(
    cache.trees.map((tree) => [tree.id, withoutFindings(tree)]),
  )
  for (const queued of [...cache.queue].sort((a, b) => a.seq - b.seq)) {
    const { operation } = queued
    if (operation.op === 'archive') {
      byId.delete(operation.id)
      continue
    }
    if (!isForVisit(operation.visitId ?? null, visit)) continue
    if (queued.snapshot === undefined) continue
    byId.set(queued.snapshot.id, queued.snapshot)
  }
  return [...byId.values()]
}

/**
 * The cache as it should read in a visit: unchanged when it already shows
 * that visit, relabeled when it shows one joined into it, and otherwise with
 * every tree's findings cleared and not fresh until the server's copy of the
 * visit comes back. A visit started on this phone (`pending`) has nothing
 * found yet, so its cleared view is fresh at once.
 *
 * Queued edits are kept, and each one remembers the tree it was made
 * against. Clearing the working set must not forget them: the phone may be
 * offline, and until the server answers, that memory is the only way back
 * to what was found. Switching back puts those trees on screen again.
 * Switching away draws them with nothing found, so one visit's jobs are
 * never filed under the next.
 *
 * A cache from before visits keeps what it shows (it is the site's one
 * migrated visit's, as likely as not), but is not fresh: it may be an
 * earlier visit's, and must not be sent as this one's.
 */
export function inVisit(
  cache: SurveyCache,
  visit: SurveyVisit | null,
): SurveyCache {
  const remembered = rememberTreeSnapshots(cache)
  const shown = remembered.visitId
  const next = visit?.id ?? null
  if (shown === next) return remembered
  if (visit !== null && shown != null && visit.joinedIds.includes(shown)) {
    return cacheWith(remembered, { visitId: next })
  }
  if (shown === undefined) {
    return cacheWith(remembered, { visitId: next, fresh: false })
  }
  return cacheWith(remembered, {
    visitId: next,
    fresh: visit?.pending === true,
    trees: treesForVisit(remembered, visit),
  })
}

/**
 * Rebuild the working set from server truth plus whatever is still queued.
 *
 * Called after a flush. The server's answer is authoritative for everything it
 * knows about — including changes made from a desk that this device never saw —
 * but it cannot know about operations recorded while the request was in flight,
 * so those are layered back on top.
 *
 * `localTrees` supplies the full record for a queued upsert when the queue
 * has no snapshot. An operation carries only what the API needs to write,
 * while the map needs a complete `TreeDto` to draw.
 *
 * Findings are visit-scoped. An edit queued for another visit must not be
 * painted onto this one: that is how last visit's jobs showed up as this
 * visit's, and how a cleared working set replaced the server's real
 * findings. A tree the server has not heard of still has to stay on the
 * map, so it is drawn with nothing found rather than dropped.
 */
export function applyQueue(
  serverTrees: TreeDto[],
  queue: QueuedOperation[],
  localTrees: TreeDto[],
  shown: SurveyVisit | null,
): TreeDto[] {
  const byId = new Map(serverTrees.map((tree) => [tree.id, tree]))

  for (const queued of [...queue].sort((a, b) => a.seq - b.seq)) {
    const { operation } = queued
    if (operation.op === 'archive') {
      byId.delete(operation.id)
      continue
    }
    const record =
      queued.snapshot ??
      localTrees.find((tree) => tree.id === operation.tree.id)
    if (record === undefined) continue
    if (isForVisit(operation.visitId ?? null, shown)) {
      byId.set(record.id, record)
      continue
    }
    if (!byId.has(operation.tree.id)) {
      byId.set(record.id, withoutFindings(record))
    }
  }

  return [...byId.values()]
}

/**
 * The label to pre-fill for the next tree: one past the highest number already
 * used. Suffixed labels ("12A") are ignored for the maximum but left untouched,
 * so hand-numbering never fights the default.
 */
export function nextLabel(trees: TreeDto[]): string {
  const highest = trees.reduce((max, tree) => {
    const digits = /^#?(\d+)/.exec(tree.label)
    const captured = digits?.[1]
    if (captured === undefined) return max
    const value = Number(captured)
    if (!Number.isInteger(value)) return max
    return Math.max(max, value)
  }, 0)
  return `#${highest + 1}`
}
